import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { SKILL_CHIPS, TECHNOLOGIES, convertPrice, getAvatarUrl } from '../data/mockData';
import { mentorService } from '../services';
import API_CONFIG from '../config/api.config';
import { useSEO, jsonLd } from '../utils/seo';
import { FollowButton, FavoriteButton } from '../components/FollowButton';
import './Mentors.css';

const SORT_OPTIONS = ['Best Match', 'Top Rated', 'Lowest Price', 'Most Reviews'];
const AVAILABILITY = ['Any Time', 'Available Today', 'This Week', 'Weekends'];
const EXPERIENCE = ['Any Level', '3–5 years', '5–10 years', '10+ years'];

export default function Mentors() {
  const { setModalOpen, currency } = useContext(AppContext);
  useSEO({
    title: 'Find a Mentor',
    description: 'Browse 2,400+ verified tech mentors from Google, Shopee, Grab and more. Filter by skill, price, and availability.',
    jsonLd: jsonLd.organization(),
  });
  const [mentors, setMentors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tech, setTech]         = useState('');
  const [budget, setBudget]     = useState('');
  const [avail, setAvail]       = useState('');
  const [exp, setExp]           = useState('');
  const [activeChips, setActiveChips] = useState([]);
  const [sort, setSort]         = useState('Best Match');
  const [viewMode, setViewMode] = useState('grid');
  const [apiError, setApiError]   = useState(false);
  const [totalFromApi, setTotalFromApi] = useState(null); // null = not loaded yet

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoading(true);
        const sortMap = { 'Top Rated': 'rating', 'Lowest Price': 'price_asc', 'Most Reviews': 'rating' };

        const budgetMap = {
          low:  { minPrice: 0,    maxPrice: 1000  },
          mid:  { minPrice: 1000, maxPrice: 3000  },
          high: { minPrice: 3000, maxPrice: 99999 },
        };
        const priceFilter = budgetMap[budget] || {};

        const expRatingMap = {
          '0-2 years':  4.0,
          '3-5 years':  4.5,
          '5+ years':   4.8,
        };
        const minRating = expRatingMap[exp] || undefined;

        const res = await mentorService.getMentors({
          search:       debouncedSearch || undefined,
          skills:       activeChips.length ? activeChips.join(',') : undefined,
          availability: avail === 'Available Today' ? 'today' : undefined,
          sort:         sortMap[sort] || undefined,
          minPrice:     priceFilter.minPrice,
          maxPrice:     priceFilter.maxPrice,
          rating:       minRating,
          page:         1,
          limit:        12,
        });
        const list = res.mentors || [];
        setApiError(false);
        setTotalFromApi(res.total ?? list.length);
        setMentors(list);
      } catch (err) {
        console.error('Failed to load mentors:', err);
        if (!API_CONFIG.MOCK) {
          setApiError(true);
          setMentors([]);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMentors();
  }, [debouncedSearch, avail, activeChips, sort, budget, exp]);

  const toggleChip = (key) => {
    setActiveChips(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const filtered = mentors;

  const resetFilters = () => {
    setSearch(''); setTech(''); setBudget(''); setAvail(''); setExp(''); setActiveChips([]);
  };

  return (
    <div className="mentors-page">
      {/* ── Top Bar ── */}
      <div className="mentors-topbar">
        <div className="mentors-topbar-inner">
          <div className="mentors-topbar-left">
            <div className="section-label" style={{marginBottom:0}}>Find Mentors</div>
            <h1 className="mentors-title">Browse <span className="mentors-count-highlight">{filtered.length}</span> Verified Mentors</h1>
          </div>
          <div className="mentors-topbar-right">
            <div className="topbar-search-wrap">
              <span className="topbar-search-icon">🔍</span>
              <input
                className="topbar-search-input"
                type="text"
                placeholder="Search name, skill, or technology…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button className="topbar-search-clear" onClick={() => setSearch('')}>✕</button>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Layout ── */}
      {apiError && (
        <div style={{ background:'#fef3c7', border:'1px solid #f59e0b', borderRadius:10, padding:'10px 18px', margin:'0 auto 16px', maxWidth:900, display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
          <span style={{fontSize:18}}>⚠️</span>
          <span><strong>Could not connect to server.</strong> Please start the backend and refresh.</span>
          <button onClick={() => window.location.reload()} style={{ marginLeft:'auto', padding:'4px 12px', background:'#f59e0b', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontWeight:600, fontSize:12 }}>Retry</button>
        </div>
      )}
      <div className="mentors-layout">

        {/* ── LEFT SIDEBAR ── */}
        <aside className="filter-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <span>Filters</span>
              <button className="reset-btn" onClick={resetFilters}>Reset all</button>
            </div>
          </div>

          {/* Technology */}
          <div className="sidebar-section">
            <div className="sidebar-label">Technology</div>
            <select className="sidebar-select" value={tech} onChange={e => setTech(e.target.value)}>
              {TECHNOLOGIES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Budget */}
          <div className="sidebar-section">
            <div className="sidebar-label">Budget / Session</div>
            <select className="sidebar-select" value={budget} onChange={e => setBudget(e.target.value)}>
              <option value="">Any Budget</option>
              <option value="low">Under {currency === 'INR' ? '₹1,000' : currency === 'USD' ? '$12' : currency === 'MYR' ? 'RM55' : 'S$16'}</option>
              <option value="mid">Mid range</option>
              <option value="high">Premium</option>
            </select>
          </div>

          {/* Availability */}
          <div className="sidebar-section">
            <div className="sidebar-label">Availability</div>
            <div className="sidebar-radio-list">
              {AVAILABILITY.map(a => (
                <label key={a} className="sidebar-radio">
                  <input type="radio" name="avail" value={a} checked={avail === a} onChange={() => setAvail(a)} />
                  <span>{a}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div className="sidebar-section">
            <div className="sidebar-label">Experience Level</div>
            <div className="sidebar-radio-list">
              {EXPERIENCE.map(e => (
                <label key={e} className="sidebar-radio">
                  <input type="radio" name="exp" value={e} checked={exp === e} onChange={() => setExp(e)} />
                  <span>{e}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="sidebar-section">
            <div className="sidebar-label">Skills</div>
            <div className="sidebar-chips">
              {SKILL_CHIPS.map(c => (
                <button
                  key={c.key}
                  className={`sidebar-chip ${activeChips.includes(c.key) ? 'on' : ''}`}
                  onClick={() => toggleChip(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="sidebar-cta">
            <div className="sidebar-cta-ico">🎓</div>
            <div className="sidebar-cta-title">Get Certified</div>
            <p>Build a project, pass a live review, and earn a blockchain certificate employers trust.</p>
            <a href="/certifications" className="btn btn-primary btn-sm" style={{width:'100%',justifyContent:'center',display:'flex',marginTop:12}}>View Tracks</a>
          </div>
        </aside>

        {/* ── RIGHT CONTENT ── */}
        <main className="mentors-main">
          {/* Chips + Sort bar */}
          <div className="mentors-controls">
            <div className="chip-scroll">
              {SKILL_CHIPS.map(c => (
                <button key={c.key} className={`chip ${activeChips.includes(c.key) ? 'active' : ''}`} onClick={() => toggleChip(c.key)}>
                  {c.label}
                </button>
              ))}
            </div>
            <div className="mentors-sort">
              <span className="sort-label">Sort:</span>
              {SORT_OPTIONS.map(s => (
                <button key={s} className={`sort-tab ${sort === s ? 'on' : ''}`} onClick={() => setSort(s)}>{s}</button>
              ))}
              <div className="view-toggle">
                <button className={`view-btn ${viewMode === 'grid' ? 'on' : ''}`} onClick={() => setViewMode('grid')}>⊞</button>
                <button className={`view-btn ${viewMode === 'list' ? 'on' : ''}`} onClick={() => setViewMode('list')}>☰</button>
              </div>
            </div>
          </div>

          {/* Result count */}
          <div className="mentors-meta-bar">
            <span className="result-count">
              {activeChips.length > 0 || search || avail ? (
                <>Showing <strong>{filtered.length}</strong> mentors</>
              ) : (
                <><strong>{filtered.length}</strong> verified mentors ready to help</>
              )}
            </span>
            {(activeChips.length > 0 || search || avail) && (
              <button className="clear-filters-btn" onClick={resetFilters}>✕ Clear filters</button>
            )}
          </div>

          {/* Mentor Cards */}
          {loading && mentors.length > 0 && (
            <div style={{ textAlign: 'center', padding: '8px 0', color: '#6b7280', fontSize: 13 }}>
              Updating results…
            </div>
          )}
          {loading && mentors.length === 0 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 36 }}>⏳</div>
              <div style={{ color: '#6b7280', fontSize: 15 }}>Loading mentors…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="no-results">
              {totalFromApi === 0 && !search && activeChips.length === 0 ? (
                // DB is empty — no mentors onboarded yet
                <>
                  <div style={{fontSize:'3rem',marginBottom:12}}>👥</div>
                  <h3>No Mentors Yet</h3>
                  <p style={{color:'#6b7280',maxWidth:320,margin:'0 auto'}}>
                    No mentors have joined the platform yet. Check back soon or become the first mentor!
                  </p>
                  <a href="/become-a-mentor" className="btn btn-primary" style={{marginTop:16,display:'inline-block'}}>Become a Mentor</a>
                </>
              ) : (
                // Filters returned no match
                <>
                  <div style={{fontSize:'3rem',marginBottom:12}}>🔍</div>
                  <h3>No mentors found</h3>
                  <p>Try adjusting your filters or search term.</p>
                  <button className="btn btn-primary" style={{marginTop:16}} onClick={resetFilters}>Clear All Filters</button>
                </>
              )}
            </div>
          ) : (
            <div className={`mentors-grid ${viewMode}`}>
              {filtered.map(m => (
                <MentorCard key={m.id} mentor={m} currency={currency} onBook={() => setModalOpen(true)} viewMode={viewMode} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function MentorCard({ mentor: m, currency, onBook, viewMode }) {
  const navigate = useNavigate();
  const price = convertPrice(m.priceINR, currency);
  const avatarUrl = getAvatarUrl(m.avatarSeed, m.avatarBg);

  if (viewMode === 'list') {
    return (
      <div className="mentor-card-list" onClick={() => navigate(`/mentors/${m.slug}`, { state: { mentor: m } })}>
        <div className="mcl-avatar-wrap">
          <img src={avatarUrl} alt={m.name} className="mcl-avatar" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
          <div className="mcl-avatar-fallback" style={{background:`#${m.avatarBg}`}}>{m.initials}</div>
          {m.isAvailableToday && <span className="online-dot" title="Available today"></span>}
        </div>
        <div className="mcl-body">
          <div className="mcl-header">
            <div>
              <div className="mc-name">{m.name}</div>
              <div className="mc-role">{m.role} · <span style={{color:'var(--ink4)'}}>{m.company}</span></div>
              <div className="mc-location">{m.location}</div>
            </div>
            <div className="mcl-right">
              {m.badge && <span className={`badge badge-${m.badgeType}`}>{m.badge}</span>}
              <div className="mc-price-big">{price}<span>/hr</span></div>
              <div className="mc-avail-lbl" style={{color: m.availabilityColor}}>{m.availabilityLabel}</div>
            </div>
          </div>
          <div className="mc-skills" style={{margin:'10px 0'}}>
            {m.skills.map(s => <span key={s} className="badge badge-gray">{s}</span>)}
          </div>
          <div className="mcl-footer">
            <div className="mc-rating">
              <span className="stars">{'★'.repeat(Math.round(m.rating))}</span>
              <span className="mc-rating-val">{m.rating} ({m.reviewCount} reviews) · {m.sessionsCompleted} sessions</span>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}} onClick={e => e.stopPropagation()}>
              <FavoriteButton mentorId={m.id} size="sm" />
              <FollowButton mentorId={m.id} size="sm" />
              <Link to={`/mentors/${m.slug}`} state={{ mentor: m }} className="btn btn-outline btn-sm">View Profile</Link>
              <Link to={`/mentors/${m.slug}/book`} className="btn btn-primary btn-sm">Book Session</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mentor-card card card-hover" onClick={() => navigate(`/mentors/${m.slug}`, { state: { mentor: m } })}>
      {/* Avatar + badge */}
      <div className="mc-avatar-section">
        <div className="mc-avatar-wrap">
          <img src={avatarUrl} alt={m.name} className="mc-avatar" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
          <div className="mc-avatar-fallback" style={{background:`#${m.avatarBg}`}}>{m.initials}</div>
          {m.isAvailableToday && <span className="online-dot" title="Available today"></span>}
        </div>
        <div className="mc-avatar-right">
          <div className="mc-name">{m.name}</div>
          <div className="mc-role">{m.role}</div>
          <div className="mc-company-pill">
            <span>{m.companyLogo}</span> {m.company} · {m.experience} yrs
          </div>
        </div>
        {m.badge && <span className={`badge badge-${m.badgeType} mc-badge`}>{m.badge}</span>}
      </div>

      {/* Rating */}
      <div className="mc-rating">
        <span className="stars">{'★'.repeat(Math.round(m.rating))}</span>
        <span className="mc-rating-val">{m.rating} ({m.reviewCount})</span>
        <span className="mc-sessions-pill">{m.sessionsCompleted} sessions</span>
      </div>

      {/* Bio */}
      <p className="mc-bio">{(m.bio || '').slice(0, 90)}{m.bio?.length > 90 ? '…' : ''}</p>

      {/* Skills */}
      <div className="mc-skills">
        {m.skills.slice(0, 4).map(s => <span key={s} className="badge badge-gray">{s}</span>)}
        {m.skills.length > 4 && <span className="badge badge-gray">+{m.skills.length - 4}</span>}
      </div>

      {/* Location */}
      <div className="mc-location">{m.location}</div>

      {/* Footer */}
      <div className="mc-footer" onClick={e => e.stopPropagation()}>
        <div>
          <div className="mc-price-big">{price}<span>/hr</span></div>
          <div className="mc-avail-lbl" style={{color: m.availabilityColor}}>{m.availabilityLabel}</div>
        </div>
        <div style={{display:'flex',gap:6,flexDirection:'column',alignItems:'flex-end'}}>
          <div style={{display:'flex',gap:6}}>
            <FavoriteButton mentorId={m.id} size="sm" />
            <FollowButton mentorId={m.id} size="sm" />
          </div>
          <Link to={`/mentors/${m.slug}/book`} className="btn btn-primary btn-sm" style={{whiteSpace:'nowrap'}}>Book Session</Link>
          <Link to={`/mentors/${m.slug}`} state={{ mentor: m }} className="btn btn-outline btn-sm" style={{whiteSpace:'nowrap'}}>View Profile</Link>
        </div>
      </div>
    </div>
  );
}
